import { stringify as yamlStringify, parse as yamlParse } from 'yaml';
import { TpDataContext, UNBOUND_COLUMN } from '../interfaces';
import { TpChartDataBinding } from '../../features/visual-analytics/charts/chartDataBindings';
import { TpActiveEnSelectionFilter, TpEnSelectionFilterStep } from '../selection/interfaces';
import {
  ParentAlignment,
  TpFilterSchemaDTO,
  TpLayoutProperties,
  TpSectionElementDTO,
  TpWidgetDTO,
  TpDashboardTemplateContentDTO,
  TpDashboardTemplateDTO,
  DASHBOARD_DEF_SCHEMA_VERSION,
  TpInlineWidgetElementDTO,
} from './schemas';
import { TpElementLayoutHints } from '../../features/visual-analytics/dashboard/layout-hints';
import { getEnSelectionFilterDef } from '../selection/selectionEnFilters';
import { DASHBOARD_TEMPLATE_ELEMENT_TYPES as ELEMENT_TYPES } from './constants';
import {
  DASHBOARDELEMIDS,
  TpActionButtonsDefinition,
  TpBitmapDefinition,
  TpChartDefinition,
  TpDashboardDefinition,
  TpDashboardElement,
  TpDashboardHorizontalLayoutDefinition,
  TpDashboardSectionDefinition,
  TpDashboardSequenceLayoutDefinition,
  TpDashboardTabberDefinition,
  TpDashboardVerticalLayoutDefinition,
  TpStaticTextDefinition,
} from '../../features/visual-analytics/dashboard/interfaces';
import { TpAssetInfo } from '../assets/interfaces';

/**
 * Reverses the process of creating an active selection filter from its JSON representation.
 * - Converts each step using the toJson method of its step definition.
 * - Filters out any undefined steps resulting from missing toJson methods.
 * At the moment there is only a single step type supported.
 * Ways of adding or modifying a filter rely on an action dbActionStepSetChartFilter,
 * which also uses the same fromJSON logic, so we should be fine.
 */
export function dehydrateFilter(
  dataContext: TpDataContext,
  dataTableId: string,
  filter: TpActiveEnSelectionFilter,
): TpFilterSchemaDTO | undefined {
  if (filter.steps.length === 0) return undefined;
  const steps = filter.steps
    .map((step: TpEnSelectionFilterStep) => {
      const stepDefinition = getEnSelectionFilterDef(step.type);
      if (stepDefinition.toJson) {
        return stepDefinition.toJson(dataContext, dataTableId, step);
      }
      console.warn(`No toJson method for step type: ${step.type}`);
      return undefined;
    })
    .filter((step) => step !== undefined);
  return steps.length > 0 ? { steps } : undefined;
}

/**
 * Cleans aspect bindings, reversing the createElement process.
 * - Removes any aspect bindings that reference the UNBOUND_COLUMN.
 * - Dehydrates any embedded filters using dehydrateFilter.
 */
export function dehydrateDataBinding(
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
): {
  dataBinding: TpWidgetDTO['dataBinding'];
  filter: TpFilterSchemaDTO | undefined;
} {
  const clean = structuredClone(dataBinding) as any;
  if (clean.aspectBindings) {
    for (const [k, v] of Object.entries(clean.aspectBindings)) {
      if ((v as any)?.columnId === UNBOUND_COLUMN) delete clean.aspectBindings[k];
    }
  }
  if (clean.secondAspectBindings) {
    for (const [k, v] of Object.entries(clean.secondAspectBindings)) {
      if ((v as any)?.columnId === UNBOUND_COLUMN) delete clean.secondAspectBindings[k];
    }
  }
  let filter = undefined;
  if (clean.filter) {
    filter = dehydrateFilter(dataContext, dataBinding.dataTableId, clean.filter);
    delete clean.filter;
  }
  return { dataBinding: clean, filter };
}

/**
 * Projects runtime layout hints back into supported YAML layoutProperties using parent context alignment.
 * - If the parent alignment is vertical, the fraction is derived from the relative height hint.
 * - If the parent alignment is horizontal or none, the fraction is derived from the relative width hint.
 * - The height is derived from the absolute height hint when available.
 */
export function dehydrateLayoutProperties(
  hints?: TpElementLayoutHints,
  parentAlign: ParentAlignment = 'none',
): TpLayoutProperties | undefined {
  if (!hints) return undefined;
  const layoutProps: TpLayoutProperties = {};

  if (parentAlign === 'vertical') {
    const frac = hints.sizeYRelative;
    if (frac !== undefined) layoutProps.fraction = frac;
  } else {
    const frac = hints.sizeXRelative;
    const height = hints.sizeYAbsolute;
    if (frac !== undefined) layoutProps.fraction = frac;
    if (height !== undefined) layoutProps.height = height;
  }
  return Object.keys(layoutProps).length > 0 ? layoutProps : undefined;
}

/**
 * Projects runtime layout hints for vertical layout containers back into YAML layoutProperties.
 * A vertical container typically has its height controlled by absolute size hints.
 * At the moment, relative height always falls back to the default layout fraction during
 * hydration, but it is still represented in the layout hints.
 */
export function dehydrateVerticalContainerLayoutProperties(hints?: TpElementLayoutHints): TpLayoutProperties {
  if (!hints) return {};
  const layoutProps: TpLayoutProperties = {};
  if (hints.sizeYAbsolute !== undefined) layoutProps.height = hints.sizeYAbsolute;
  const frac = hints.sizeYRelative;
  if (frac !== undefined) layoutProps.fraction = frac;
  return layoutProps;
}

/**
 * Dehydrates a widget element into the template DTO format and potentially updates the widget catalog.
 * Dehydration performs:
 * - The layout properties are derived from the element's runtime layout hints and the parent alignment.
 * - The parent alignment influences how the layout properties are interpreted.
 * - specific conversion of title and description from the chart's custom properties is reversed.
 * - data binding and filters are extracted and included in the widget DTO.
 * Returns either:
 * the unique key referencing the widget in the catalog, and updates the catalog (the key is
 * generated based on the chart's ID to ensure uniqueness within the catalog.)
 * OR the widget definition itself.
 */
function dehydrateWidgetElement(
  dataContext: TpDataContext,
  chart: TpChartDefinition,
  widgetCatalog: Record<string, TpWidgetDTO>,
  parentAlign: ParentAlignment = 'none',
): string | TpInlineWidgetElementDTO {
  const widgetKey = `widget_${chart.id}`;
  const cleanConfig = chart.config ? structuredClone(chart.config) : {};

  const title = cleanConfig.customTitle;
  const description = cleanConfig.customDescription;
  delete cleanConfig.customTitle;
  delete cleanConfig.customDescription;

  const { dataBinding, filter } = dehydrateDataBinding(dataContext, chart.dataBinding);

  const widgetDTO: TpWidgetDTO = {
    chartType: chart.chartType,
    title,
    description,
    selector: chart.selector,
    dataBinding,
    config: Object.keys(cleanConfig).length > 0 ? cleanConfig : undefined,
    filter,
    layoutProperties: dehydrateLayoutProperties(chart.elemLayoutHints, parentAlign) ?? {},
  };

  // If the chart has a selector, we store it in the widget catalog and return the key.
  if (chart.selector) {
    widgetCatalog[widgetKey] = widgetDTO;
    return widgetKey;
  }

  // Otherwise, return the widget definition itself.
  return { ...widgetDTO, type: ELEMENT_TYPES.Widget };
}

/**
 * Recursively dehydrates a dashboard element into the template DTO element structure,
 * updating the widgetCatalog with any widgets encountered.
 * It returns a dehydrated representation of the element suitable for inclusion in the template DTO.
 */
export function dehydrateElement(
  dataContext: TpDataContext,
  elem: TpDashboardElement,
  widgetCatalog: Record<string, TpWidgetDTO>,
  parentAlign: ParentAlignment = 'none',
): TpSectionElementDTO | TpSectionElementDTO[] {
  switch (elem.elemType) {
    case DASHBOARDELEMIDS.Widget: {
      const chart = elem as TpChartDefinition;
      return dehydrateWidgetElement(dataContext, chart, widgetCatalog, parentAlign);
    }
    case DASHBOARDELEMIDS.HorizontalLayout: {
      const hor = elem as TpDashboardHorizontalLayoutDefinition;

      // Horizontal layout currently has no specific handling for layout properties as
      // all horizontal layout is always generated with default layout properties during re-hydration.
      // However, we still dehydrate the layout properties for future use.
      const layoutProps = dehydrateLayoutProperties(hor.elemLayoutHints, parentAlign);

      return {
        type: ELEMENT_TYPES.LayoutHorizontal,
        layoutProperties: layoutProps,
        elements: hor.elements.flatMap((c) => dehydrateElement(dataContext, c, widgetCatalog, 'horizontal')),
      };
    }
    case DASHBOARDELEMIDS.VerticalLayout: {
      const vert = elem as TpDashboardVerticalLayoutDefinition;

      // Vertical layout has specific handling for layout properties.
      const layoutProps = dehydrateVerticalContainerLayoutProperties(vert.elemLayoutHints);
      return {
        type: ELEMENT_TYPES.LayoutVertical,
        layoutProperties: layoutProps,
        elements: vert.elements.flatMap((c) => dehydrateElement(dataContext, c, widgetCatalog, 'vertical')),
      };
    }
    case DASHBOARDELEMIDS.SequenceLayout: {
      const seq = elem as TpDashboardSequenceLayoutDefinition;
      const layoutProps = dehydrateLayoutProperties(seq.elemLayoutHints, parentAlign);
      return {
        type: ELEMENT_TYPES.LayoutSequence,
        layoutProperties: layoutProps,
        elements: seq.elements.flatMap((c) => dehydrateElement(dataContext, c, widgetCatalog, 'horizontal')),
      };
    }
    case DASHBOARDELEMIDS.Tabber: {
      const tabber = elem as TpDashboardTabberDefinition;
      return {
        type: ELEMENT_TYPES.Tabber,
        titles: tabber.tabTitles,
        layoutProperties: dehydrateLayoutProperties(tabber.elemLayoutHints, parentAlign),
        // Tabber children currently hydrate through the horizontal fallback.
        elements: tabber.elements.flatMap((c) => dehydrateElement(dataContext, c, widgetCatalog, 'horizontal')),
      };
    }
    case DASHBOARDELEMIDS.StaticText: {
      const txt = elem as TpStaticTextDefinition;
      return {
        type: ELEMENT_TYPES.Text,
        content: txt.content,
        layoutProperties: dehydrateLayoutProperties(txt.elemLayoutHints, parentAlign),
      };
    }
    case DASHBOARDELEMIDS.Bitmap: {
      const bmp = elem as TpBitmapDefinition;
      return {
        type: ELEMENT_TYPES.Bitmap,
        bitmapId: bmp.id,
        description: bmp.description || undefined,
        layoutProperties: dehydrateLayoutProperties(bmp.elemLayoutHints, parentAlign),
      };
    }
    case DASHBOARDELEMIDS.ActionButtons: {
      const act = elem as TpActionButtonsDefinition;
      // Switch from a list of action definitions to a list of action IDs
      return {
        type: ELEMENT_TYPES.ActionButtons,
        actions: act.actionDefs.map((a: any) => (typeof a === 'string' ? a : a.id)),
      };
    }
    default:
      throw new Error(`Unsupported element type: ${(elem as any).elemType}`);
  }
}

/**
 * Serializes a runtime Redux dashboard definition into a template content DTO.
 */
export function serializeDashboardToTemplateContentDTO(
  dataContext: TpDataContext,
  dashboardDef: TpDashboardDefinition,
): TpDashboardTemplateContentDTO {
  const widgetCatalog: Record<string, TpWidgetDTO> = {};
  const originalTemplateInfo = yamlParse(dashboardDef.templateInfo.yamlString);
  const originalTemplateInfoContent = originalTemplateInfo.templateContent;

  const sections = dashboardDef.elements.map((sec: TpDashboardSectionDefinition) => ({
    id: sec.id,
    title: sec.title,
    description: sec.description || undefined,
    requiredTableId: sec.requiredTableId || undefined,
    collapsed: sec.collapsed || false,
    elements: sec.elements.flatMap((c) => dehydrateElement(dataContext, c, widgetCatalog, 'none')),
  }));

  return {
    version: originalTemplateInfoContent.version,
    displayName: originalTemplateInfoContent.displayName,
    assetType: originalTemplateInfoContent.assetType,
    templateId: originalTemplateInfoContent.templateId,
    elements: {
      topbar: dashboardDef.topbarTables?.tableSelection?.map((id) => ({ id })),
      widgetGlobalConfig:
        dashboardDef.widgetGlobalConfig && Object.keys(dashboardDef.widgetGlobalConfig).length > 0
          ? dashboardDef.widgetGlobalConfig
          : undefined,
      actions:
        dashboardDef.dashboardActionDefs && Object.keys(dashboardDef.dashboardActionDefs).length > 0
          ? dashboardDef.dashboardActionDefs
          : undefined,
      widgets: widgetCatalog,
      sections,
    },
  };
}

/**
 * Serializes a runtime Redux dashboard definition into a YAML template string.
 */
export function serializeDashboardToYaml(dataContext: TpDataContext, dashboardDef: TpDashboardDefinition): string {
  const templateObj = serializeDashboardToTemplateContentDTO(dataContext, dashboardDef);

  return yamlStringify(
    {
      templateContent: templateObj,
      templateScope: 'dashboard',
      templateType: dashboardDef.templateInfo.templateType,
    },
    { indent: 2 },
  );
}

/**
 * Serializes a runtime Redux dashboard definition into a DTO object for copy-pasting
 * and saving to a view state.
 */
export function mapReduxToDashboardTemplateDTO(
  dataContext: TpDataContext,
  dashboardDef: TpDashboardDefinition,
  assetInfo: TpAssetInfo,
): TpDashboardTemplateDTO {
  const templateContent = serializeDashboardToTemplateContentDTO(dataContext, dashboardDef);
  return {
    version: DASHBOARD_DEF_SCHEMA_VERSION,
    assetType: assetInfo.type,
    sourceAssetId: assetInfo.id,
    sourceAssetName: assetInfo.displayName,
    baseTemplateId: dashboardDef.templateInfo.id,
    templateContent,
  };
}
